import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { areFieldsDifferent } from '../../../../../shared/frontend/libs/utils';
import { TApplicationState } from '../libs/store';
import { apiServiceAsync, TGetKeystoneSpecRankingsParams } from '../services/apiService';

import { TAction } from '../../../../../shared/frontend/scripts/actions/types/action';
import KeystoneActionCreator from '../actions/keystoneActionCreator';
import frontendLog from '../libs/frontendLog';

export const useKeystoneSpecRankingsData = (params: TGetKeystoneSpecRankingsParams) => {
  const dispatch = useDispatch();
  const mergedParams = useMemo(() => params, [JSON.stringify(params)]);
  const keystoneCharacterRankings = useSelector((state: TApplicationState) => state.keystoneCharacterRankings);
  const instanceId = JSON.stringify(mergedParams);
  const state = keystoneCharacterRankings.instances[instanceId] || {};
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const checkFields = ['season', 'region', 'page', 'class', 'spec', 'realm', 'faction'];
    const contentChanged = !state.ui || areFieldsDifferent(checkFields, mergedParams, state.ui);

    const loadHandler = async () => {
      try {
        setIsLoading(true);
        const result = await apiServiceAsync.getKeystoneSpecRankings(mergedParams);

        if (!result) {
          throw new Error('getKeystoneSpecRankings got empty result');
        }

        dispatch(KeystoneActionCreator.setKeystoneCharacterRankings(result.rankings, instanceId));
      } catch (error) {
        dispatch(KeystoneActionCreator.setKeystoneCharacterRankingsError(instanceId, error));
        frontendLog.error({ err: error }, 'getKeystoneSpecRankings failed');
      } finally {
        setIsLoading(false);
      }
    };

    if (!state.isLoading && (!state.rankedCharacters || contentChanged)) {
      loadHandler();
    }
  }, [mergedParams]);

  return { ...state, isLoading };
};

export const createServerPreloadActionsToPreloadSpecRankings =
  (params: TGetKeystoneSpecRankingsParams) => async (): Promise<TAction<any>[]> => {
    try {
      const instanceId = JSON.stringify(params);
      const result = await apiServiceAsync.getKeystoneSpecRankings(params);
      return [KeystoneActionCreator.setKeystoneCharacterRankings(result.rankings, instanceId)];
    } catch (error) {
      return [];
    }
  };
